/** SUNTRONIX ONE — ONE Before decision panel.
 *
 *  Reusable component that surfaces FACTS · OPTIONS · WHY before a major
 *  lifecycle action.  Do NOT create separate modules for each action — drop
 *  <OneBefore> inline at the moment of decision.
 *
 *  Props:
 *    action        "buy" | "replace" | "repair" | "recycle" | "deploy" | …
 *    masterCompanyId  tenant scope
 *    quantity      requested quantity (buy / procurement flows)
 *    model         optional model narrowing
 *    assetId       optional record id (recycle / repair flows)
 *    forEmployeeName  optional display context
 *    onChoose(option)    called when user selects an option
 *    onContinue()        called when user proceeds with original action
 */
import { useEffect, useState } from "react";
import { AlertCircle, ArrowRight, Check, ChevronDown, ChevronRight, Loader2, ShieldCheck, Sparkles, X } from "lucide-react";
import api from "@/lib/api";

const ACTION_LABEL = {
  buy: "Before you buy",
  deploy: "Before you deploy",
  replace: "Before you replace",
  repair: "Before you repair",
  store: "Before you store",
  refresh: "Before you refresh",
  recycle: "Before you recycle",
  approve: "Before you approve",
  ship: "Before you ship",
  recover: "Before you recover",
  order_accessories: "Before you order accessories",
};

export default function OneBefore({
  action,
  masterCompanyId,
  quantity = 1,
  model,
  ramGb,
  storageGb,
  assetId,
  forEmployeeName,
  onChoose,
  onContinue,
  onDismiss,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!masterCompanyId || !action) { setLoading(false); return; }
    setLoading(true);
    api.post("/decisions/context", {
      action, master_company_id: masterCompanyId, quantity,
      model, ram_gb: ramGb, storage_gb: storageGb, asset_id: assetId,
    })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [action, masterCompanyId, quantity, model, ramGb, storageGb, assetId]);

  const logChoice = async (option) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/decisions/log", {
        action, chosen_option: option.key, master_company_id: masterCompanyId,
        presented_options: data?.options || [], facts: data?.facts || {},
      });
      onChoose?.(option);
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="st-panel p-4 text-[12px] text-[color:var(--st-text-muted)]" data-testid="one-before-loading">
      <Loader2 className="inline h-3.5 w-3.5 animate-spin mr-1.5" /> Checking existing evidence…
    </div>;
  }
  if (!data) return null;

  const anyStock = (data.options || []).some((o) => (o.count ?? 0) > 0);
  const title = ACTION_LABEL[action] || "Before you act";

  return (
    <div
      className="st-panel st-panel-lift p-5 border-[color:var(--st-accent-soft)]"
      data-testid={`one-before-${action}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-[color:var(--st-accent-soft)] text-[color:var(--st-accent)] grid place-items-center shrink-0">
          <Sparkles className="h-5 w-5" strokeWidth={1.6} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-accent)]">ONE Before</div>
          <div className="text-[15px] font-semibold text-[color:var(--st-text)] mt-0.5">{title}</div>
          <div className="text-[12px] text-[color:var(--st-text-muted)] mt-0.5">
            {data.explanation}
            {forEmployeeName ? ` · for ${forEmployeeName}` : ""}
          </div>
        </div>
        <button onClick={() => setExpanded((v) => !v)} className="text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]"
          aria-label={expanded ? "Collapse" : "Expand"}>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {onDismiss && (
          <button onClick={onDismiss} aria-label="Dismiss" data-testid="one-before-dismiss"
            className="text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {expanded && (
        <>
          <FactsRow facts={data.facts} action={action} />

          <div className="mt-3 space-y-2" data-testid="one-before-options">
            {(data.options || []).map((o) => {
              const isRecommended = data.recommend && o.key === data.recommend;
              const disabled = o.count === 0;
              return (
                <div key={o.key} className={
                  "flex items-start gap-3 px-3 py-2.5 rounded-md border " +
                  (isRecommended
                    ? "border-emerald-300 bg-emerald-50"
                    : "border-[color:var(--st-border)] bg-white")
                }
                  data-testid={`one-before-option-${o.key}`}>
                  <div className="mt-0.5">
                    {isRecommended
                      ? <Check className="h-4 w-4 text-emerald-700" />
                      : <ShieldCheck className="h-4 w-4 text-[color:var(--st-text-muted)]" strokeWidth={1.6} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <div className="text-[13px] font-medium text-[color:var(--st-text)]">{o.label}</div>
                      {typeof o.count === "number" && (
                        <div className={"text-[11px] tabular-nums " + (o.count > 0 ? "text-[color:var(--st-accent)] font-semibold" : "text-[color:var(--st-text-hint)]")}>
                          {o.count} available
                        </div>
                      )}
                      {isRecommended && (
                        <span className="ml-auto text-[9px] uppercase tracking-[0.18em] bg-emerald-100 text-emerald-800 px-1.5 rounded font-semibold">Recommended</span>
                      )}
                    </div>
                    <ul className="mt-1 text-[11px] text-[color:var(--st-text-muted)] space-y-0.5">
                      {(o.why || []).map((r) => <li key={r}>· {r}</li>)}
                    </ul>
                  </div>
                  <button
                    onClick={() => logChoice(o)}
                    disabled={disabled || busy}
                    data-testid={`one-before-choose-${o.key}`}
                    className={
                      "h-8 px-3 rounded-md text-[11px] font-medium shrink-0 " +
                      (disabled
                        ? "text-[color:var(--st-text-hint)] cursor-not-allowed"
                        : isRecommended
                        ? "bg-emerald-700 text-white hover:bg-emerald-800"
                        : "bg-[color:var(--st-text)] text-white hover:bg-[#17263C]")
                    }>
                    Choose <ArrowRight className="inline h-3 w-3 ml-0.5" />
                  </button>
                </div>
              );
            })}
          </div>

          {onContinue && (
            <div className="mt-4 pt-4 border-t border-[color:var(--st-border)] flex items-center gap-2">
              <button onClick={onContinue}
                data-testid="one-before-continue"
                className="h-8 px-3 rounded-md text-[12px] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]">
                Continue with {ACTION_LABEL[action]?.replace(/^Before you /i, "") || "action"}
              </button>
              {!anyStock && (
                <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-[color:var(--st-warn)]">
                  <AlertCircle className="h-3 w-3" /> ONE found no existing supply to consider.
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FactsRow({ facts, action }) {
  const items = [
    { k: "ready_for_reuse",   l: "Ready for reuse" },
    { k: "new_stock",         l: "New stock" },
    { k: "ready_to_deploy",   l: "Ready to deploy" },
    { k: "spare_pool",        l: "Spare pool" },
    { k: "loaner_pool",       l: "Loaner pool" },
    { k: "incoming_po",       l: "Incoming PO" },
    { k: "in_repair",         l: "In repair" },
  ];
  return (
    <div className="mt-4 grid grid-cols-3 md:grid-cols-7 gap-2" data-testid="one-before-facts">
      {items.map((it) => (
        <div key={it.k} className="rounded-md bg-[color:var(--st-surface-alt)] px-2.5 py-2">
          <div className="text-[9px] uppercase tracking-[0.14em] text-[color:var(--st-text-muted)]">{it.l}</div>
          <div className="text-[16px] font-heading font-semibold tabular-nums text-[color:var(--st-text)]">{facts?.[it.k] ?? 0}</div>
        </div>
      ))}
    </div>
  );
}
